/**
 * SHIPPING ESTIMATE COUNTDOWN
 * ----------------------------------------------------------------------------------------------------------------
 * Fills in [data-shipping-countdown] elements with a live "order within Xh Ym to receive it <date>" message.
 * Cutoff is a fixed 8:00 AM in Europe/Madrid time (CET in winter, CEST in summer — using the real IANA zone
 * instead of a hardcoded UTC+2 offset means this stays correct across the DST switch, not just in summer).
 * Delivery is always 5 business days (Mon-Fri) counted from whichever day's cutoff is currently being counted
 * down to.
 */
(() => {
  const TIMEZONE = 'Europe/Madrid';
  const CUTOFF_HOUR = 8;
  const BUSINESS_DAYS_TO_DELIVER = 5;

  // Represents "wall-clock time in Europe/Madrid" as a UTC-based Date, so day/hour arithmetic (setUTCHours,
  // setUTCDate) behaves predictably regardless of the visitor's own timezone. The difference between two such
  // fake-UTC values still matches the real elapsed time, as long as no DST shift falls inside that window —
  // the one night that happens, the countdown can be off by an hour, which is fine for a delivery estimate.
  function madridWallClockAsUtc(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
      .formatToParts(date)
      .reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {});

    return new Date(
      Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        // Some engines format midnight as "24" instead of "00" with hour12:false.
        Number(parts.hour) === 24 ? 0 : Number(parts.hour),
        Number(parts.minute),
        Number(parts.second)
      )
    );
  }

  function nextCutoff(madridNow) {
    const cutoff = new Date(madridNow);
    cutoff.setUTCHours(CUTOFF_HOUR, 0, 0, 0);

    if (madridNow.getTime() >= cutoff.getTime()) {
      cutoff.setUTCDate(cutoff.getUTCDate() + 1);
    }

    return cutoff;
  }

  function addBusinessDays(date, days) {
    const result = new Date(date);
    let added = 0;

    while (added < days) {
      result.setUTCDate(result.getUTCDate() + 1);
      const day = result.getUTCDay(); // 0 = Sunday, 6 = Saturday

      if (day !== 0 && day !== 6) added++;
    }

    return result;
  }

  function formatCountdown(ms) {
    const totalMinutes = Math.max(0, Math.round(ms / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}h ${minutes}m`;
  }

  function formatDeliveryDate(fakeUtcDate) {
    // Rebuild a real date at noon UTC from the Y/M/D so weekday/day/month formatting can't get shifted a
    // day by the visitor's own timezone when Intl renders it.
    const real = new Date(Date.UTC(fakeUtcDate.getUTCFullYear(), fakeUtcDate.getUTCMonth(), fakeUtcDate.getUTCDate(), 12));

    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      timeZone: 'UTC',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(real);
  }

  function update(el) {
    const template = el.dataset.template;
    if (!template) return;

    const madridNow = madridWallClockAsUtc(new Date());
    const cutoff = nextCutoff(madridNow);
    const deliveryDate = addBusinessDays(cutoff, BUSINESS_DAYS_TO_DELIVER);

    const time = formatCountdown(cutoff.getTime() - madridNow.getTime());
    const date = formatDeliveryDate(deliveryDate);

    // innerHTML, not textContent — the template wraps __DATE__ in <strong> (see product-info.liquid) so the
    // delivery date renders bold. time/date are both computed here, not user input, so this is safe.
    el.innerHTML = template.replace('__TIME__', time).replace('__DATE__', date);
  }

  function refreshAll() {
    document.querySelectorAll('[data-shipping-countdown]').forEach(update);
  }

  function init() {
    if (window.__shippingEstimateInit) return;
    window.__shippingEstimateInit = true;

    refreshAll();
    setInterval(refreshAll, 60000);

    // The cart drawer is re-rendered through the Section Rendering API after every cart change, which swaps in
    // a fresh (empty) [data-shipping-countdown] element — and scripts inside that HTML never re-run. Watch for
    // new empty ones and fill them in; :empty makes sure our own innerHTML write can't retrigger this.
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        document.querySelectorAll('[data-shipping-countdown]:empty').forEach(update);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
